import React, { useMemo, useState } from "react";

const PRODUCT_COPY = {
  "HW-TRAVEL-KIT": {
    eyebrow: "Travel systems",
    price: 110,
    compareAt: 119,
    description: "A considered three-piece set for lighter weekends: canvas carryall, insulated bottle, and packing cubes.",
    detail: "Designed for short Australian escapes, with hard-wearing canvas and practical organisation."
  },
  "HW-DESK-LAMP": {
    eyebrow: "Home office",
    price: 89,
    description: "Warm, focused light in a compact olive and walnut form made for quiet corners.",
    detail: "A compact ambient task lamp with a weighted timber base and warm, glare-conscious light."
  }
};

function money(value) {
  return new Intl.NumberFormat("en-AU", { style: "currency", currency: "AUD", maximumFractionDigits: 0 }).format(value || 0);
}

export default function Storefront({ data, assets, cart, setCart, lastOrder, busy, error, onCheckout, onOpenOperations }) {
  const [selectedSku, setSelectedSku] = useState("");
  const [cartOpen, setCartOpen] = useState(false);
  const [checkoutOpen, setCheckoutOpen] = useState(false);
  const products = data ? data.catalog.filter((item) => PRODUCT_COPY[item.sku]) : [];
  const selected = products.find((item) => item.sku === selectedSku);
  const count = cart.reduce((sum, item) => sum + item.quantity, 0);
  const subtotal = useMemo(() => cart.reduce((sum, item) => {
    const product = products.find((candidate) => candidate.sku === item.sku);
    return sum + (PRODUCT_COPY[item.sku]?.price || product?.priceAud || 0) * item.quantity;
  }, 0), [cart, products]);

  const add = (sku) => {
    setCart((current) => {
      const existing = current.find((item) => item.sku === sku);
      return existing
        ? current.map((item) => item.sku === sku ? { ...item, quantity: Math.min(4, item.quantity + 1) } : item)
        : [...current, { sku, quantity: 1 }];
    });
    setCartOpen(true);
  };

  const change = (sku, delta) => {
    setCart((current) => current
      .map((item) => item.sku === sku ? { ...item, quantity: item.quantity + delta } : item)
      .filter((item) => item.quantity > 0)
      .map((item) => ({ ...item, quantity: Math.min(4, item.quantity) })));
  };

  const place = async () => {
    const ok = await onCheckout(cart);
    if (ok) {
      setCheckoutOpen(false);
      setCartOpen(false);
    }
  };

  return (
    <div className="store-page">
      <section className="store-hero">
        <div className="hero-copy">
          <span className="kicker">New field collection · Australia</span>
          <h1>Good objects for<br />unhurried days.</h1>
          <p>Travel and home essentials selected for useful rituals, tactile materials, and long weekends.</p>
          <div className="hero-actions">
            <button className="primary" onClick={() => setSelectedSku("HW-TRAVEL-KIT")}>Shop the travel kit</button>
            <button className="text-button" onClick={onOpenOperations}>See how orders are operated →</button>
          </div>
        </div>
        <div className="hero-product">
          <img src={assets["HW-TRAVEL-KIT"]} alt="Coastal Travel Kit with canvas bag, packing cubes, and bottle" />
          <div className="hero-caption"><span>01</span><strong>Coastal Travel Kit</strong><span>{money(110)}</span></div>
        </div>
      </section>

      <section className="assurance">
        <span>Free demo delivery over A$100</span><span>Australian dispatch simulation</span><span>30-day demo returns</span>
      </section>

      {lastOrder && (
        <section className="order-success" aria-live="polite">
          <div><span className="kicker">Sandbox order confirmed</span><h2>Thanks, Alex. Your order is in.</h2><p>{lastOrder.id} · {money(lastOrder.totalAud || lastOrder.priceAud)} · Payment was not charged.</p></div>
          <button className="outline" onClick={onOpenOperations}>Follow it in operations</button>
        </section>
      )}

      <section className="collection">
        <div className="section-heading"><div><span className="kicker">Harbor & Wattle field goods</span><h2>Shop the collection</h2></div><p>Two original products, one complete commerce journey.</p></div>
        <div className="product-grid">
          {products.map((product) => {
            const copy = PRODUCT_COPY[product.sku];
            return (
              <article className="product-card" key={product.sku}>
                <button className="product-image" onClick={() => setSelectedSku(product.sku)} aria-label={"View " + product.name}>
                  <img src={assets[product.sku]} alt={product.name} />
                  {copy.compareAt && <span className="sale-tag">Launch price</span>}
                </button>
                <div className="product-copy">
                  <span className="product-eyebrow">{copy.eyebrow}</span>
                  <div className="product-title"><h3>{product.name}</h3><strong>{money(copy.price)}</strong></div>
                  <p>{copy.description}</p>
                  <div className="product-actions"><button className="outline" onClick={() => setSelectedSku(product.sku)}>View details</button><button className="primary" onClick={() => add(product.sku)}>Add to bag</button></div>
                </div>
              </article>
            );
          })}
        </div>
      </section>

      <section className="brand-story">
        <span className="kicker">Made for the demo, built for the proof</span>
        <h2>A storefront a customer can use. An operating system a business owner can inspect.</h2>
        <p>The transaction here is intentionally sandboxed, but the product, cart, checkout, persisted order, policy decision, and audit trail are connected end to end.</p>
      </section>

      <button className="floating-cart" onClick={() => setCartOpen(true)}>Bag <span>{count}</span></button>

      {selected && (
        <div className="modal-backdrop" onClick={() => setSelectedSku("")}>
          <section className="product-modal" onClick={(event) => event.stopPropagation()} role="dialog" aria-modal="true" aria-label={selected.name}>
            <button className="close" onClick={() => setSelectedSku("")}>×</button>
            <img src={assets[selected.sku]} alt={selected.name} />
            <div className="modal-copy"><span className="product-eyebrow">{PRODUCT_COPY[selected.sku].eyebrow}</span><h2>{selected.name}</h2><strong className="modal-price">{money(PRODUCT_COPY[selected.sku].price)}</strong><p>{PRODUCT_COPY[selected.sku].detail}</p><ul><li>Supplier availability checked</li><li>{selected.stockOnHand} demo units on hand</li><li>Ships in {selected.sku === "HW-TRAVEL-KIT" ? "four" : "two"} simulated days</li></ul><button className="primary wide" onClick={() => { add(selected.sku); setSelectedSku(""); }}>Add to bag</button></div>
          </section>
        </div>
      )}

      {cartOpen && (
        <div className="drawer-backdrop" onClick={() => setCartOpen(false)}>
          <aside className="cart-drawer" onClick={(event) => event.stopPropagation()} aria-label="Shopping bag">
            <div className="drawer-head"><div><span className="kicker">Your selection</span><h2>Shopping bag</h2></div><button className="close" onClick={() => setCartOpen(false)}>×</button></div>
            {cart.length === 0 ? <div className="empty-cart"><p>Your bag is ready for something useful.</p><button className="outline" onClick={() => setCartOpen(false)}>Continue shopping</button></div> : (
              <>
                <div className="cart-lines">
                  {cart.map((item) => {
                    const product = products.find((candidate) => candidate.sku === item.sku);
                    const price = PRODUCT_COPY[item.sku]?.price || product?.priceAud || 0;
                    return <div className="cart-line" key={item.sku}><img src={assets[item.sku]} alt="" /><div><strong>{product?.name}</strong><small>{money(price)} each</small><div className="quantity"><button onClick={() => change(item.sku, -1)}>−</button><span>{item.quantity}</span><button onClick={() => change(item.sku, 1)}>+</button></div></div><strong>{money(price * item.quantity)}</strong></div>;
                  })}
                </div>
                <div className="cart-total"><span>Subtotal</span><strong>{money(subtotal)}</strong></div>
                <small className="sandbox-note">Sandbox checkout: no card is requested and no payment can be captured.</small>
                <button className="primary wide" onClick={() => setCheckoutOpen(true)}>Continue to checkout</button>
              </>
            )}
          </aside>
        </div>
      )}

      {checkoutOpen && (
        <div className="modal-backdrop" onClick={() => setCheckoutOpen(false)}>
          <section className="checkout-modal" onClick={(event) => event.stopPropagation()} role="dialog" aria-modal="true" aria-label="Sandbox checkout">
            <button className="close" onClick={() => setCheckoutOpen(false)}>×</button>
            <div className="checkout-copy"><span className="kicker">Secure sandbox checkout</span><h2>Delivery details</h2><p>This fixed demo profile prevents visitors from submitting personal information.</p>
              <div className="form-grid">
                <label>Full name<input value="Alex Morgan (demo customer)" readOnly /></label>
                <label>Email<input value="alex.morgan@example.test" readOnly /></label>
                <label>Suburb<input value="Fitzroy" readOnly /></label>
                <label>State / postcode<input value="VIC 3065" readOnly /></label>
              </div>
              <div className="payment-box"><span>Payment</span><strong>Sandbox · no charge</strong><small>No payment provider is connected.</small></div>
              {error && <p className="inline-error">{error}</p>}
            </div>
            <div className="checkout-summary"><span className="kicker">Order summary</span>{cart.map((item) => <div className="summary-line" key={item.sku}><span>{item.quantity + " × " + (products.find((p) => p.sku === item.sku)?.name || item.sku)}</span><strong>{money((PRODUCT_COPY[item.sku]?.price || 0) * item.quantity)}</strong></div>)}<div className="summary-line total"><span>Total</span><strong>{money(subtotal)}</strong></div><button className="primary wide" onClick={place} disabled={busy === "checkout"}>{busy === "checkout" ? "Placing sandbox order…" : "Place sandbox order"}</button><small className="sandbox-note">By continuing, you create a synthetic persisted order only.</small></div>
          </section>
        </div>
      )}
    </div>
  );
}